import { Utils } from './../../../helpers/utils.helper'
import { Store } from '@ngxs/store'
import {
  IWarning,
  ProductTypes,
  WarningActions,
  WarningReferences,
} from '../smart-suggestion.model'
import { SmartSuggestionMetaState } from '../smart-suggestion.meta.state'
import { RemoveWarningAction } from '../smart-suggestion.meta.actions'
import { Injectable } from '@angular/core'
import { QuoteMetaState } from '../../quote/quote.meta.state'

@Injectable({ providedIn: 'root' })
export class AccessoriesHelper {
  constructor(
    private store: Store,
    private utils: Utils,
  ) {}

  findAccessoriesWarningReasons(
    reference: WarningReferences,
  ): IWarning | undefined {
    const warnings = this.store.selectSnapshot(
      SmartSuggestionMetaState.getWarnings,
    )
    const foundWarning = warnings.find(
      (warning) => warning.reference === reference,
    )
    return foundWarning
  }

  validateAccessoriesConstraints() {
    const bracketPanelReasons = this.getValidationsFromBracketXPanel()
    const accessoryInverterReasons = this.getValidationsFromAccessoryXInverter()
    if (bracketPanelReasons.length > 0 || accessoryInverterReasons.length > 0) {
      return [...bracketPanelReasons, ...accessoryInverterReasons]
    }
    return []
  }

  getValidationsFromAccessoryXInverter() {
    const selectedAccessories = this.store.selectSnapshot(
      QuoteMetaState.getSelectedAccessories,
    )
    const selectedInverters = this.store.selectSnapshot(
      QuoteMetaState.getSelectedInverters,
    )
    const batteriesSapCodes = [
      '12215760-00',
      '12215816-00',
      '12036768-00',
      '11882485-00',
    ]
    const allowedInverterOperationModes = ['HYBRID', 'OFF_GRID']
    const targetBatteries = selectedAccessories.filter((accessory: any) =>
      batteriesSapCodes.includes(accessory.sapCode),
    )
    const targetInverters = selectedInverters.filter((inverter: any) =>
      allowedInverterOperationModes.includes(inverter.inverterOperationMode),
    )
    const isAnyBatterySelected = targetBatteries.length > 0
    const isAnyInverterSelected = targetInverters.length > 0
    if (isAnyBatterySelected && !isAnyInverterSelected) {
      const content = `${this.utils.getTranslatedKey(
        'You cannot buy batteries without an inverter with operation mode hybrid or off-grid. Please add an inverter to your quote.',
      )}`
      const reason = {
        content,
        isRestrictive: true,
        lockedTabs: [7],
        options: [],
        reference:
          WarningReferences.INVERTER_X_ACCESSORIES_MISSING_REQUIRED_ACCESSORIES,
      }
      return [reason]
    }
    const foundWarning = this.findAccessoriesWarningReasons(
      WarningReferences.INVERTER_X_ACCESSORIES_MISSING_REQUIRED_ACCESSORIES,
    )
    if (foundWarning) {
      this.store.dispatch(new RemoveWarningAction(foundWarning.reference))
    }
    return []
  }

  getValidationsFromBracketXPanel() {
    const accessories = this.store.selectSnapshot(QuoteMetaState.getAccessories)
    const targetAccessoryCode = 'I8000070163'
    const matchAccessory = accessories.find(
      (accessory: any) => accessory.sapCode === targetAccessoryCode,
    )
    const compositionSuggestions = this.store.selectSnapshot(
      SmartSuggestionMetaState.getCompositionSuggestions,
    )
    if (!compositionSuggestions) {
      return []
    }
    const accessoriesSuggestions = compositionSuggestions.accessories
    const matchSuggestion = accessoriesSuggestions.find(
      (suggestion) => suggestion?.content?.[0]?.id === matchAccessory?.id,
    )
    const isLessThanMinimum =
      matchAccessory?.quantity < matchSuggestion?.content?.[0]?.quantity
    if (isLessThanMinimum) {
      const content = `${this.utils.getTranslatedKey(
        'You need to add at least',
      )} ${matchSuggestion?.content[0].quantity}x ${this.utils.getTranslatedKey(
        'of',
      )} ${matchAccessory.title} ${this.utils.getTranslatedKey(
        'to protect your panels',
      )}, ${this.utils.getTranslatedKey(
        'please select the necessary accessories before proceeding to checkout.',
      )}`
      const reason = {
        content,
        isRestrictive: true,
        lockedTabs: [7],
        options: [
          {
            actions: [WarningActions.SEE_SUGGESTIONS],
            title: 'See Suggestions',
          },
        ],
        reference:
          WarningReferences.PANEL_X_BRACKETS_MISSING_REQUIRED_ACCESSORIES,
      }
      return [reason]
    }
    const foundWarning = this.findAccessoriesWarningReasons(
      WarningReferences.PANEL_X_BRACKETS_MISSING_REQUIRED_ACCESSORIES,
    )
    if (foundWarning) {
      this.store.dispatch(new RemoveWarningAction(foundWarning.reference))
    }
    return []
  }

  getAccessoriesSuggestions() {
    const bracketPanelsSuggestions = this.getSuggestionsFromBracketXPanel()
    return bracketPanelsSuggestions
  }

  getSuggestionsFromBracketXPanel() {
    const selectedPanel = this.store.selectSnapshot(
      QuoteMetaState.getSelectedPanel,
    )
    const selectedBrackets = this.store.selectSnapshot(
      QuoteMetaState.getSelectedBrackets,
    )
    const accessories = this.store.selectSnapshot(QuoteMetaState.getAccessories)
    const targetAccessoryCode = 'I8000070163'
    const matchAccessory = accessories.find(
      (accessory: any) => accessory?.sapCode === targetAccessoryCode,
    )
    const targetPanelSapCode = 'BYD-540MLK-36-I'
    const matchPanel = selectedPanel?.sapCode === targetPanelSapCode
    const targetBracketsSapCode = ['I8000070022', 'I8000070020']
    const foundBrackets = selectedBrackets.filter((bracket: any) => {
      return targetBracketsSapCode.includes(bracket?.sapCode)
    })
    if (
      !selectedPanel ||
      selectedBrackets.length === 0 ||
      accessories.length === 0 ||
      !matchAccessory ||
      !matchPanel ||
      foundBrackets.length === 0
    ) {
      return []
    }
    const totalMinimumAmount = selectedPanel.quantity * 4
    const isSelected = matchAccessory.quantity === totalMinimumAmount
    const option = {
      title: `${totalMinimumAmount}x ${matchAccessory.title}`,
      isSelected,
      content: [
        {
          id: matchAccessory.id,
          type: ProductTypes.ACCESSORY,
          quantity: totalMinimumAmount,
        },
      ],
    }
    return [option]
  }
}
